import { createContext, useContext, type ReactNode } from 'react';

/**
 * Bizy analytics event names used by the renderer node/card components.
 * Mirrors the `AnalyticsEvent.Bizy.*` values in Kaleidoscope so the host
 * app can forward them to its own analytics pipeline unchanged.
 */
export const BizyAnalyticsEvent = {
	EvidenceDrilled: 'Bizy Evidence Drilled',
	ChartExported: 'Bizy Chart Exported',
	TableExported: 'Bizy Table Exported',
	TableSorted: 'Bizy Table Sorted',
	SourceDetailsToggled: 'Bizy Source Details Toggled',
	FollowUpClicked: 'Bizy Follow Up Clicked',
	MeetingPeopleCardClicked: 'Bizy Meeting People Card Clicked',
	GifPickerOpened: 'Bizy Gif Picker Opened',
	GifPillClicked: 'Bizy Gif Pill Clicked',
	GifSelected: 'Bizy Gif Selected',
	PersonPickerSelected: 'Bizy Person Picker Selected',
} as const;

export type BizyTrackFn = (event: string, properties?: Record<string, unknown>) => void;

interface BizyAnalytics {
	/** Stamped on every evidence interaction so events join to a conversation/message. */
	conversationId?: string;
	messageId?: string;
	/** Injected by the host. No-op by default so cards render standalone. */
	track: BizyTrackFn;
}

const noop: BizyTrackFn = () => {};

const BizyAnalyticsContext = createContext<BizyAnalytics>({ track: noop });

export function BizyAnalyticsProvider({
	conversationId,
	messageId,
	track = noop,
	children,
}: {
	conversationId?: string;
	messageId?: string;
	track?: BizyTrackFn;
	children: ReactNode;
}) {
	return <BizyAnalyticsContext.Provider value={{ conversationId, messageId, track }}>{children}</BizyAnalyticsContext.Provider>;
}

export function useBizyAnalytics(): BizyAnalytics {
	return useContext(BizyAnalyticsContext);
}
